// ReadReceiptModal — 메신저 '읽음 표시' 클릭 시 뜨는 수신 확인 팝업.
//
// 설계: `C:\aigw-designs\patch_messenger_read_receipts.md` §3
//
// 왜 게이트웨이가 그리는가: 라벨은 MM iframe **안**에 주입하지만 팝업은 iframe **밖**에서
// 우리가 그린다. MM DOM 안에 팝업을 만들면 MM 이 버전업할 때마다 같이 깨진다.
//
// 🔴 '읽음' 은 **그 사람이 채널을 연 시각**으로 판정한 근사다(글을 봤다는 보장이 아니다).
//    JANDI 도 같은 방식인데, 그 사실을 화면이 숨기면 사람이 숫자를 과신한다 — 그래서 적는다.
import { useEffect, useState } from 'react';

const AVATAR_BG = '#e9e7e2';

function Row({ p }) {
  const [broken, setBroken] = useState(false);
  const initial = (p.name || '?').trim().charAt(0);
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 4px' }}>
      {p.avatar && !broken ? (
        <img src={p.avatar} alt="" width={34} height={34} onError={() => setBroken(true)}
          style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', background: AVATAR_BG, flexShrink: 0 }} />
      ) : (
        <span aria-hidden="true" style={{ width: '34px', height: '34px', borderRadius: '50%', background: AVATAR_BG, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, color: '#6b6a66' }}>{initial}</span>
      )}
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '13.5px', color: '#1f1e1c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.name}
          {p.position ? <span style={{ color: '#8a8880', fontSize: '12.5px', marginLeft: '5px' }}>{p.position}</span> : null}
        </span>
        {p.dept ? <span style={{ display: 'block', fontSize: '12px', color: '#8a8880', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.dept}</span> : null}
      </span>
    </li>
  );
}

export default function ReadReceiptModal({ data, onClose }) {
  const [tab, setTab] = useState('read');
  // 다른 글을 눌렀는데 아까 보던 탭이 남아 있으면 "왜 미확인이 먼저 뜨지" 가 된다.
  useEffect(() => { setTab('read'); }, [data]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!data) return null;
  const list = tab === 'read' ? (data.read || []) : (data.unread || []);
  const tabStyle = (on) => ({
    flex: 1, padding: '10px 8px', fontSize: '13px', fontWeight: on ? 700 : 500,
    color: on ? '#c6613f' : '#6b6a66', background: 'none', border: 'none',
    borderBottom: `2px solid ${on ? '#c6613f' : 'transparent'}`, cursor: 'pointer',
  });

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2147483600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box' }}
    >
      <div role="dialog" aria-modal="true" aria-label="메시지 수신" data-testid="gw-read-receipts"
        style={{ width: '360px', maxWidth: '92vw', maxHeight: '78vh', background: '#fff', borderRadius: '14px', boxShadow: '0 10px 40px rgba(0,0,0,.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px' }}>
          <b style={{ fontSize: '14.5px', color: '#1f1e1c' }}>메시지 수신</b>
          <button type="button" onClick={onClose} aria-label="닫기"
            style={{ background: 'none', border: 'none', fontSize: '18px', lineHeight: 1, color: '#8a8880', cursor: 'pointer', padding: '0 2px' }}>×</button>
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid #eceae6' }}>
          <button type="button" style={tabStyle(tab === 'read')} onClick={() => setTab('read')}>
            수신 확인 {data.read_count}
          </button>
          <button type="button" style={tabStyle(tab === 'unread')} onClick={() => setTab('unread')}>
            미확인 {data.unread_count}
          </button>
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: '6px 14px 10px', overflowY: 'auto', flex: 1 }}>
          {list.map((p) => <Row key={p.mm_id} p={p} />)}
          {!list.length && (
            <li style={{ padding: '22px 4px', textAlign: 'center', fontSize: '13px', color: '#8a8880' }}>
              {tab === 'read' ? '아직 아무도 확인하지 않았습니다.' : '모두 확인했습니다.'}
            </li>
          )}
        </ul>
        <p style={{ margin: 0, padding: '9px 16px 12px', borderTop: '1px solid #eceae6', fontSize: '11.5px', color: '#8a8880', lineHeight: 1.5 }}>
          ‘수신 확인’은 그 사람이 <b>이 글 이후 채널을 열었다</b>는 뜻입니다. 글을 실제로 읽었는지까지는 알 수 없습니다.
        </p>
      </div>
    </div>
  );
}
